/**
 * Utility functions for ISBN/UPC code processing
 */

/**
 * Normalize and convert various barcode formats to ISBN-13
 * Handles ISBN-10, ISBN-13, UPC-A, UPC-E, and other formats
 */
export function normalizeBarcodeToISBN(code: string): string | null {
  if (!code) {
    return null;
  }

  // Remove hyphens and spaces
  const cleaned = code.replace(/[-\s]/g, '').trim();

  // Must be all digits
  if (!/^\d+$/.test(cleaned)) {
    return null;
  }

  const length = cleaned.length;

  // ISBN-13 (13 digits) - already correct format
  if (length === 13) {
    return cleaned;
  }

  // ISBN-10 (10 digits) - convert to ISBN-13
  if (length === 10) {
    // Add 978 prefix and calculate check digit
    const isbn13Base = '978' + cleaned.substring(0, 9);
    const checkDigit = calculateISBN13CheckDigit(isbn13Base);
    return isbn13Base + checkDigit;
  }

  // UPC-A (12 digits) - convert to ISBN-13
  if (length === 12) {
    // If it starts with 0, it's a UPC-A, remove leading 0 and add 978
    if (cleaned.startsWith('0')) {
      const isbn13Base = '978' + cleaned.substring(1, 11); // Remove leading 0 and check digit
      const checkDigit = calculateISBN13CheckDigit(isbn13Base);
      return isbn13Base + checkDigit;
    } else {
      // Might be EAN-13 without check digit, try adding 978
      const isbn13Base = '978' + cleaned.substring(0, 11); // Remove last digit
      const checkDigit = calculateISBN13CheckDigit(isbn13Base);
      return isbn13Base + checkDigit;
    }
  }

  // UPC-E (8 digits) - try to expand
  if (length === 8) {
    // Simplified UPC-E expansion: pad with zeros
    // Proper UPC-E expansion is more complex, but this handles common cases
    const padded = '0'.repeat(4) + cleaned; // Pad to 12 digits
    if (padded.length === 12) {
      const isbn13Base = '978' + padded.substring(1, 11);
      const checkDigit = calculateISBN13CheckDigit(isbn13Base);
      return isbn13Base + checkDigit;
    }
  }

  // For codes that are too short, return null
  if (length < 10) {
    return null;
  }

  // Try to handle other lengths by adding 978 prefix
  if (length > 10 && length < 13) {
    const paddingNeeded = 13 - length;
    if (paddingNeeded > 0) {
      const isbn13Base = '978' + '0'.repeat(paddingNeeded - 1) + cleaned.substring(0, length - 1);
      const checkDigit = calculateISBN13CheckDigit(isbn13Base);
      return isbn13Base + checkDigit;
    }
  }

  return null;
}

/**
 * Calculate ISBN-13 check digit
 */
function calculateISBN13CheckDigit(isbn12: string): string {
  let total = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(isbn12[i], 10);
    const multiplier = i % 2 === 0 ? 1 : 3;
    total += digit * multiplier;
  }
  const remainder = total % 10;
  const checkDigit = (10 - remainder) % 10;
  return checkDigit.toString();
}

/**
 * Validate if a string looks like a valid ISBN/UPC code
 */
export function isValidBarcode(code: string): boolean {
  if (!code) {
    return false;
  }

  const cleaned = code.replace(/[-\s]/g, '').trim();
  
  // Must be all digits and between 8-13 characters
  if (!/^\d+$/.test(cleaned) || cleaned.length < 8 || cleaned.length > 13) {
    return false;
  }

  return true;
}

/**
 * Format ISBN for display (add hyphens)
 */
export function formatISBN(isbn: string): string {
  if (!isbn) {
    return '';
  }

  const cleaned = isbn.replace(/[-\s]/g, '');

  // ISBN-13 format: 978-1-234-56789-0
  if (cleaned.length === 13) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 4)}-${cleaned.substring(4, 7)}-${cleaned.substring(7, 12)}-${cleaned.substring(12)}`;
  }

  // ISBN-10 format: 1-234-56789-0
  if (cleaned.length === 10) {
    return `${cleaned.substring(0, 1)}-${cleaned.substring(1, 4)}-${cleaned.substring(4, 9)}-${cleaned.substring(9)}`;
  }

  return cleaned;
}

